import { Vector3 } from 'three';
import { Side } from '../../@types/Side';
import { UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import Entity, { IEntity } from './Entity';
import { IWall } from './Wall';

export interface IBoundaryEdge extends IEntity {
  wall: IWall;
  t0: number;
  t1: number;
  side: Side;
  reversed?: boolean;

  setPoints(start: Vector3, end: Vector3): void;
  
  resetPoints(): void;

  readonly startPoint: Vector3;
  readonly endPoint: Vector3;
  readonly displayLength: number; // for UI
}

class BoundaryEdge extends Entity implements IBoundaryEdge {
  wall: IWall;
  t0: number;
  t1: number;
  side: Side;
  reversed?: boolean;

  // We use simple names for the backing fields
  private _startPoint: Vector3 | null = null;
  private _endPoint: Vector3 | null = null;

  constructor(wall: IWall, t0 = 0, t1 = 1, side: Side = 'A', reversed = false) {
    super();
    this.wall = wall;
    this.t0 = t0;
    this.t1 = t1;
    this.side = side;
    this.reversed = reversed;
    this.clamp();
  }

  /**
   * Save the actual calculated points.
   */
  setPoints(start: Vector3, end: Vector3) {
    this._startPoint = start;
    this._endPoint = end;
  }

  resetPoints() {
    this._startPoint = null;
    this._endPoint = null;
  }

  clamp() {
    const lo = Math.max(0, Math.min(1, Math.min(this.t0, this.t1)));
    const hi = Math.max(0, Math.min(1, Math.max(this.t0, this.t1)));
    this.t0 = lo;
    this.t1 = hi;
  }

  /**
   * Returns the actual start point.
   * If setPoints() was called, returns that value.
   * Otherwise, calculates position along the parent Wall.
   */
  get startPoint(): Vector3 {
    if (this._startPoint) return this._startPoint;
    return this.wall.pointAt(this.reversed ? this.t1 : this.t0);
  }

  /**
   * Returns the actual end point.
   */
  get endPoint(): Vector3 {
    if (this._endPoint) return this._endPoint;
    return this.wall.pointAt(this.reversed ? this.t0 : this.t1);
  }

  get displayLength(): number {
    return this.startPoint.distanceTo(this.endPoint) / UNIT_TO_METER_DIVISOR;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      wall: { uuid: this.wall.uuid },
      t0: this.t0,
      t1: this.t1,
      side: this.side,
      reversed: !!this.reversed,
      startPoint: this._startPoint, 
      endPoint: this._endPoint,
    };
  }
}

export default BoundaryEdge;